import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";

const STORAGE_KEY = "mx_lang_v1";
const DEFAULT_LANG = "id"; // Indonesian by default

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "en" || stored === "id") return stored;
    } catch (e) { console.warn("Could not read language preference from localStorage:", e); }
    return DEFAULT_LANG;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { console.warn("Could not persist language preference to localStorage:", e); }
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key, fallback) => {
    const dict = translations[lang] || translations[DEFAULT_LANG];
    return dict[key] ?? translations.en[key] ?? fallback ?? key;
  }, [lang]);

  const toggle = useCallback(() => setLang((prev) => (prev === "id" ? "en" : "id")), []);

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, toggle, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
};
